import { useCallback, useMemo, useState } from 'react';
import type { RowSelectionState } from '@tanstack/react-table';
import type { Connector } from '@/types/connectors';

function rowIdOf(c: Connector): string {
  return String(c.id ?? c.connectorType);
}

export interface UseConnectorSelectionResult {
  rowSelection: RowSelectionState;
  setRowSelection: (state: RowSelectionState) => void;
  count: number;
  visibleCount: number;
  isAllVisibleSelected: boolean;
  isSomeVisibleSelected: boolean;
  toggleAllVisible: () => void;
  toggleRow: (rowId: string) => void;
  clear: () => void;
  selectedConnectors: (allConnectors: Connector[]) => Connector[];
}

export function useConnectorSelection(
  visibleConnectors: Connector[]
): UseConnectorSelectionResult {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const visibleIds = useMemo(
    () => visibleConnectors.map(rowIdOf),
    [visibleConnectors]
  );

  const count = useMemo(
    () => Object.values(rowSelection).filter(Boolean).length,
    [rowSelection]
  );

  const visibleSelectedIds = useMemo(
    () => visibleIds.filter((id) => rowSelection[id]),
    [visibleIds, rowSelection]
  );

  const visibleCount = visibleSelectedIds.length;
  const isAllVisibleSelected =
    visibleIds.length > 0 && visibleSelectedIds.length === visibleIds.length;
  const isSomeVisibleSelected =
    visibleSelectedIds.length > 0 && !isAllVisibleSelected;

  const toggleRow = useCallback((rowId: string) => {
    setRowSelection((prev) => {
      const next = { ...prev };
      if (next[rowId]) delete next[rowId];
      else next[rowId] = true;
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setRowSelection((prev) => {
      const allVisibleSelected =
        visibleIds.length > 0 &&
        visibleIds.every((id) => prev[id]);
      const next = { ...prev };
      if (allVisibleSelected) {
        // Deselect only the visible rows; off-screen stays selected.
        for (const id of visibleIds) delete next[id];
      } else {
        for (const id of visibleIds) next[id] = true;
      }
      return next;
    });
  }, [visibleIds]);

  const clear = useCallback(() => setRowSelection({}), []);

  const selectedConnectors = useCallback(
    (allConnectors: Connector[]) =>
      allConnectors.filter((c) => rowSelection[rowIdOf(c)]),
    [rowSelection]
  );

  return {
    rowSelection,
    setRowSelection,
    count,
    visibleCount,
    isAllVisibleSelected,
    isSomeVisibleSelected,
    toggleAllVisible,
    toggleRow,
    clear,
    selectedConnectors,
  };
}
